// -------------------------------
// TICKET CATEGORY INTERFACES
// -------------------------------
export interface PendingTicketCategory {
    name?: string;
    status?: string;
}

export interface PendingDeleteTicketCategory {
    awaitingName?: boolean;
}

export const defaultStatus = "active";

export const startCreateTicketCategoryFlow = (
    setPending: (data: PendingTicketCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({
            name: nameFromIntent,
            status: defaultStatus
        });

        pushAIChat(
            `Got it! Creating ticket category "${nameFromIntent}". Should I keep status as Active?`
        );
        speak(`Creating ticket category ${nameFromIntent}. Do you want status active?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the ticket category be called?");
    speak("What should the ticket category be called?");
};

export const processPendingTicketCategory = (
    text: string,
    pending: PendingTicketCategory,
    setPending: (data: PendingTicketCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — CATEGORY NAME
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid ticket category name.");
            speak("Please provide a valid ticket category name.");
            return;
        }

        setPending({ ...pending, name: cleanName });

        pushAIChat(`Should "${cleanName}" be Active or Inactive?`);
        speak(`Should ${cleanName} be active or inactive?`);
        return;
    }

    // STEP 2 — STATUS
    if (!pending.status) {
        const status = text.includes("inactive") ? "inactive" : "active";

        pushAIChat(
            `Creating ticket category "${pending.name}" with status ${status}...`
        );
        speak(`Creating ticket category ${pending.name}`);

        window.dispatchEvent(
            new CustomEvent("ai-create-ticket-category", {
                detail: {
                    name: pending.name,
                    status
                }
            })
        );

        setPending(null);
        return;
    }
};

export const startDeleteTicketCategoryFlow = (
    setPending: (data: PendingDeleteTicketCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting ticket category "${nameFromIntent}"...`);
        speak(`Deleting ticket category ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-ticket-category", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingName: true });
    pushAIChat("Which ticket category would you like to delete?");
    speak("Which ticket category would you like to delete?");
};

export const processPendingDeleteTicketCategory = (
    text: string,
    pending: PendingDeleteTicketCategory,
    setPending: (data: PendingDeleteTicketCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid ticket category name.");
        speak("Please provide a valid ticket category name.");
        return;
    }

    pushAIChat(`Deleting ticket category "${cleanName}"...`);
    speak(`Deleting ticket category ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-ticket-category", {
            detail: { name: cleanName }
        })
    );

    setPending(null);
};

